import { useState, useCallback, useEffect } from "react"
import { toast } from "sonner"
import { ShieldCheck, UserRoundCog  } from 'lucide-react'
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import ChangePassword from "../../cors/ChangePassword";
import { BASE_URL } from '../../apiConfig'


export default function UpdateAdminForm({ userId, currentUserData, roles: availableRoles ,fetchData}) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    phone: "",
    email: "",
    role: 1,
    selectedRoleId: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [serverError, setServerError] = useState("")

  const roleOptions = [
    { value: 1, label: t("header.role_admin"), icon: ShieldCheck },
    { value: 2, label: t("header.role_assistant"), icon: UserRoundCog },
  ]

  useEffect(() => {
    if (currentUserData) {
      setFormData({
        name: currentUserData.info?.name || "",
        gender: currentUserData.info?.gender || "",
        phone: currentUserData.info?.phone ? String(currentUserData.info.phone) : "",
        email: currentUserData.email || "",
        role: currentUserData.type || 1,
        selectedRoleId: currentUserData.roles?.[0]?.id || null
      })
    }
  }, [currentUserData])

const handleInputChange = useCallback((e) => {
  const { name, value, type } = e.target;
  let parsedValue = value;

  // Always parse to number for role + selectedRoleId
  if (name === "role" || name === "selectedRoleId" || type === "number") {
    parsedValue = Number(value);
  }

  setFormData(prev => {
    let updated = { ...prev, [name]: parsedValue };

    // If role changes
    if (name === "role") {
      if (parsedValue === 1) {
        // Switching to Admin clears assistant role
        updated.selectedRoleId = null;
      }
    }

    return updated;
  });

  if (errors[name]) {
    setErrors(prev => ({ ...prev, [name]: "" }));
  }
  setServerError("");
}, [errors]);

  const validateEmail = (email) => {
    return /^[^\s.]+.[^\s.]+\.[^\s.]+$/.test(email)
  }

  const validateForm = useCallback(() => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = t("admins.validation.email_required")
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("admins.validation.email_valid")
    }

    if (!formData.name.trim()) {
      newErrors.name = t("admins.validation.name_required")
    }

    if (!formData.gender) {
      newErrors.gender = t("admins.validation.gender_required")
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("admins.validation.phone_required")
    } else if (!/^\d+$/.test(formData.phone.trim())) {
      newErrors.phone = t("admins.validation.phone_number")
    }

    if (formData.role === 2 && !formData.selectedRoleId) {
      newErrors.selectedRoleId = t("admins.validation.role_required")
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    setServerError("")

    try {
      const payload = {
        name: formData.name,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email,
        role: formData.role,
        ...(formData.role === 2 && formData.selectedRoleId && {
          specific_role_id: formData.selectedRoleId,
        }),
      }

      const response = await fetch(`${BASE_URL}/api/admins/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          const validationErrors = {}
          for (const key in data.errors) {
            validationErrors[key] = data.errors[key][0]
          }
          setErrors(validationErrors)
          setServerError(t("admins.errors.form_errors"))
        } else {
          throw new Error(data.message || "Failed to update user")
        }
        return
      }

      toast.success(t("admins.success_update"))
      fetchData()
      setIsEditing(false)
    } catch (error) {
      console.error("Error updating user:", error)
      setServerError(error.message || t("admins.server_connection_failed"))
      toast.error(t("admins.errors.update_failed"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: currentUserData.info?.name || "",
      gender: currentUserData.info?.gender || "",
      phone: currentUserData.info?.phone ? String(currentUserData.info.phone) : "",
      email: currentUserData.email || "",
      role: currentUserData.type || 1,
      selectedRoleId: currentUserData.roles?.[0]?.id || null
    })
    setIsEditing(false)
    setErrors({})
    setServerError("")
  }

  return (
    <div className="max-w-5xl mx-auto min-h-screen px-4">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                  {currentUserData.type === 1 ?
                    <ShieldCheck className="h-10 w-10 md:w-16 md:h-16 text-white" />
                    :
                    <UserRoundCog className="h-10 w-10 md:w-16 md:h-16 text-white" />
                  }
                </div>
                <div className="text-white">
                  <h1 className="text-xl md:text-2xl font-bold">{currentUserData.type === 1 ? t("admins.title_admin") : t("admins.title_assistant")}</h1>
                  <p className="text-emerald-100 text-sm">{t("admins.subtitle")}</p>
                  <p className="text-xs text-emerald-100 mt-1">
                    {t("admins.role_label")} {currentUserData.type === 1 ? t("header.role_admin") : t("header.role_assistant")}
                  </p>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                    <div className="text-xl font-bold">{currentUserData.type === 1 ? t("header.role_admin") : t("header.role_assistant")}</div>
                  <div className="text-xs text-emerald-500">{t("admins.access_level")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {serverError && (
            <div className="bg-red-500 text-white p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-lg font-semibold">{t("messages.error")} !</span>
              </div>
              <p className="text-sm">{serverError}</p>
            </div>
          )}

          <div className="p-8 max-xl:p-4">
            <div className="flex justify-end py-5 space-x-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <span className="text-sm max-xl:text-xs">{t("admins.edit_profile")}</span>
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center space-x-2 ${
                      isLoading ? 'opacity-70' : ''
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="max-xl:text-xs">{t("admins.saving")}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">{t("buttons.save")}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t("buttons.cancel")}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="p-8 max-xl:p-4">
                <div className="flex items-center mb-6">
                  <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">{t("admins.personal_info")}</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
                      {t("forms.labels.email")}
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="utilisateur.example.com"
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.email
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.email || t("admins.not_specified")}
                      </p>
                    )}
                    {errors.email && <p className="mt-2 text-red-600 text-xs">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-600 mb-2">
                      {t("admins.full_name_label")}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t("admins.name_placeholder")}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.name
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info?.name || t("admins.not_specified")}
                      </p>
                    )}
                    {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-600 mb-2">
                      {t("forms.labels.gender")}
                    </label>
                    {isEditing ? (
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.gender
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      >
                        <option value="">{t("admins.select_gender")}</option>
                        <option value="Male">{t("admins.male")}</option>
                        <option value="Female">{t("admins.female")}</option>
                      </select>
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info?.gender === "Male" ? t("admins.male") : currentUserData?.info?.gender === "Female" ? t("admins.female") : t("admins.not_specified")}
                      </p>
                    )}
                    {errors.gender && <p className="mt-2 text-red-600 text-xs">{errors.gender}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-600 mb-2">
                      {t("forms.labels.phone")}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t("admins.phone_placeholder")}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.phone
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info?.phone || t("admins.not_specified")}
                      </p>
                    )}
                    {errors.phone && <p className="mt-2 text-red-600 text-xs">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              <div className="p-8 max-xl:px-5">
                <div className="flex items-center mb-6">
                  <div className="bg-[var(--color-primary-500)]/15 p-3 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">{t("admins.admin_role_section")}</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      {t("forms.labels.role_type")}
                    </label>
                    {isEditing ? (
                      <div className="grid grid-cols-2 gap-4">
                        {roleOptions.map((option) => (
                          <label
                            key={option.value}
                            className={`px-6 py-5 rounded-2xl cursor-pointer transition-all duration-300 border-2 hover:shadow-lg ${
                              formData.role === option.value
                                ? "bg-[var(--color-primary-50)] border-[var(--color-primary-50)]0 text-[var(--color-primary-500)] shadow-lg"
                                : "bg-gray-50 border-gray-200 hover:border-[var(--color-primary-50)]0 text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              name="role"
                              value={option.value}
                              checked={formData.selectedRoleId === formData.role.id}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className="text-center flex-1 space-y-1">
                              <div className=" opacity-75 flex justify-center"><option.icon size={35} /> </div>
                              <div className="font-semibold text-sm">{option.label}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="pt-4">
                        {currentUserData?.type === 1 ? 
                        <div className="bg-gradient-to-r text-center w-25 flex-1 rounded-2xl py-2 text-white from-emerald-500 via-teal-500 to-cyan-500 ">
                          <ShieldCheck className="w-10 h-10 mx-auto" />
                          {t("header.role_admin")}
                        </div>
                        : 
                        <div className="bg-gradient-to-r text-center w-25 flex-1 rounded-2xl py-2 text-white from-orange-500 via-orange-400 to-orange-300">
                          <UserRoundCog  className="w-10 h-10 mx-auto" />
                          {t("header.role_assistant")}
                        </div>
                        }
                      </div>
                    )}
                  </div>

                  {formData.role === 2 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">{t("forms.sections.assistant_role_specific")}</h3>
                      {isEditing ? (
                        <div className="grid grid-cols-2 gap-3">
                          {availableRoles.length > 0 ? (
                            availableRoles.map((role) => (
                              <label
                                key={role.id}
                                className={`flex items-center p-4 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${
                                  formData.selectedRoleId === role.id
                                    ? "bg-[var(--color-primary-50)] border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                                    : "bg-gray-50 border-gray-200 hover:border-[var(--color-primary-50)]0 text-gray-700"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="selectedRoleId"
                                  value={role.id}
                                  checked={formData.selectedRoleId === role.id}
                                  onChange={handleInputChange}
                                  className="w-5 h-5 text-[var(--color-primary-500)] border-gray-300 focus:ring-[var(--color-primary-500)] mr-3"
                                />
                                <span className="font-medium text-xs">{role.name}</span>
                              </label>
                            ))
                          ) : (
                            <p className="text-gray-500 text-xs">{t("admins.no_role_available")}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                          {currentUserData?.roles?.[0]?.name || t("admins.no_specific_role")}
                        </p>
                      )}
                      {errors.selectedRoleId && <p className="mt-2 text-red-600 text-xs">{errors.selectedRoleId}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <ChangePassword userId={userId} />  
      </div>
    </div>
  )
}
